import {
  useContext,
  createContext,
  useState,
  ReactNode,
  useMemo,
  useCallback,
  useEffect,
  useRef,
  RefObject,
} from 'react';

import {
  parseDate,
  CalendarNavigationType,
  getStructuredDisabledMonths,
  generateCalendarId,
} from './helpers';

export type ProviderType = {
  children: ReactNode;
  /**
   * @public
   * Автоматический фокус на первую доступную дату при открытии календаря
   */
  autofocus?: boolean;
  visible?: boolean;
  /**
   * @public
   * Дата в формате YYYY-MM-DD
   */
  initialDate?: string;
  /**
   * @public
   * Тип выбираемой даты: день, месяц или год
   */
  selectionDateType?: 'day' | 'month' | 'year';
  /**
   * @public
   * Указывает какой тип календаря отображается по умолчанию
   */
  initialCalendarType?: CalendarNavigationType;
  /**
   * @public
   * Массив дат в формате YYYY-MM-DD[]
   */
  disabledDates?: string[];
  /**
   * @public
   * Самая ранняя возможная дата в формате YYYY-MM-DD
   */
  minDate?: string;
  /**
   * @public
   * Самая поздняя возможная дата в формате YYYY-MM-DD
   */
  maxDate?: string;
  calendarId?: string;
  /**
   * @public
   * Выбранные даты в формате YYYY-MM-DD[] или undefined
   */
  selectedDate: string[] | undefined;
  selectionType?: 'day' | 'interval';
  /**
   * @public
   * Callback вызывается при изменении текущей даты или типа календаря в компоненте.
   */
  onNavigation?: (
    currentDate: string,
    calendarType: CalendarNavigationType
  ) => void;
  onSelectedDateChange: (selectedDate: string | undefined) => void;
};

type InnerDateChangeParams = { date: string; updateFocus: boolean };
type RequiredContextFields = 'disabledDates' | 'selectionType' | 'initialDate';
type ContextValue = {
  isFirstRender: boolean;
  autofocus: boolean;
  currentDate: string;
  focusedDate: string | undefined;
  structuredDisabledMonths: Record<string, number[]>;
  initialCalendarType: CalendarNavigationType;
  calendarType: CalendarNavigationType;
  calendarId: string;
  setCurrentDate: (date: string) => void;
  setFocusedDate: (date: string | undefined) => void;
  setCalendarType: (type: CalendarNavigationType) => void;
  onInnerDateChange: (props: InnerDateChangeParams) => void;
  leftNavigationButtonRef: RefObject<HTMLButtonElement>;
  rightNavigationButtonRef: RefObject<HTMLButtonElement>;
} & Omit<ProviderType, 'children' | RequiredContextFields> &
  Required<Pick<ProviderType, RequiredContextFields>>;

const CalendarContext = createContext<ContextValue | null>(null);

export const CalendarProvider = ({
  children,
  autofocus = false,
  disabledDates = [],
  visible = true,
  minDate,
  maxDate,
  initialDate = parseDate(new Date()),
  selectedDate,
  calendarId = generateCalendarId(),
  selectionDateType = 'day',
  initialCalendarType = 'days',
  selectionType = 'day',
  onNavigation,
  onSelectedDateChange,
}: ProviderType) => {
  const [currentDate, setCurrentDate] = useState<string>(initialDate);
  const [focusedDate, setFocusedDate] = useState<string>();
  const [calendarType, setCalendarType] = useState(initialCalendarType);
  const leftNavigationButtonRef = useRef<HTMLButtonElement>(null);
  const rightNavigationButtonRef = useRef<HTMLButtonElement>(null);
  const isFirstRender = useRef(true);

  const structuredDisabledMonths = useMemo(
    () => getStructuredDisabledMonths(disabledDates),
    [disabledDates]
  );

  const onInnerDateChange = useCallback(
    ({ date, updateFocus }: InnerDateChangeParams) => {
      setCurrentDate(date);
      setFocusedDate(updateFocus ? date : undefined);
    },
    []
  );

  useEffect(function deactivateIsFirstRender() {
    if (isFirstRender.current) {
      isFirstRender.current = false;
    }
  }, []);

  useEffect(
    function updateInnerDates() {
      const newCurrentDate = initialDate || parseDate(new Date());
      onInnerDateChange({ date: newCurrentDate, updateFocus: autofocus });
    },
    [initialDate]
  );

  useEffect(
    function callOnNavigation() {
      if (!isFirstRender.current && visible && onNavigation)
        onNavigation(currentDate, calendarType);
    },
    [currentDate, calendarType]
  );

  const contextValue: ContextValue = {
    isFirstRender: isFirstRender.current,
    autofocus,
    initialDate,
    currentDate,
    focusedDate,
    selectedDate,
    calendarType,
    disabledDates,
    visible,
    minDate,
    maxDate,
    structuredDisabledMonths,
    selectionDateType,
    initialCalendarType,
    selectionType,
    calendarId,
    leftNavigationButtonRef,
    rightNavigationButtonRef,
    onSelectedDateChange,
    onInnerDateChange,
    setCurrentDate,
    setFocusedDate,
    setCalendarType,
  };

  return (
    <CalendarContext.Provider value={contextValue}>
      {children}
    </CalendarContext.Provider>
  );
};

export const useCalendarContext = () => {
  const value = useContext(CalendarContext);
  if (!value) {
    throw new Error(
      'Используйте useCalendarContext внутри контекста CalendarContext'
    );
  }
  return value;
};
